/** Ground-plane area visualization for hazard zones, healing circles, barriers. */
import Phaser from 'phaser';
import { VfxPrimitive, VfxPrimitiveConfig } from './VfxPrimitive';
import { ensureSoftCircleTexture, ensureRingTexture, CRISP_SCALE } from '../../ui/CrispUtil';

export interface ZoneOverlayConfig extends VfxPrimitiveConfig {
  shape: 'circle' | 'rectangle' | 'ring';
  radius?: number;
  width?: number;
  height?: number;
  fillTint: number;
  edgeTint: number;
  edgeLineWidth: number;
  baseAlpha: number;
  pulseAlphaRange: number;
  pulsePeriodMs: number;
  warningAlpha?: number;
  warningPulsePeriodMs?: number;
  fadeDurationMs: number;
}

/** Ensure a soft-edged rectangular fill texture exists at given dimensions. */
function ensureRectFillTexture(
  scene: Phaser.Scene,
  w: number,
  h: number,
): string {
  const key = `_zone-rect-${w}x${h}`;
  if (scene.textures.exists(key)) return key;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  // Soft edge fade — 4px border gradient
  const edge = Math.min(4 * CRISP_SCALE, w / 4, h / 4);
  ctx.globalCompositeOperation = 'destination-in';
  // Top
  const gt = ctx.createLinearGradient(0, 0, 0, edge);
  gt.addColorStop(0, 'rgba(255,255,255,0)');
  gt.addColorStop(1, 'rgba(255,255,255,1)');
  ctx.fillStyle = gt;
  ctx.fillRect(0, 0, w, edge);
  // Bottom
  const gb = ctx.createLinearGradient(0, h - edge, 0, h);
  gb.addColorStop(0, 'rgba(255,255,255,1)');
  gb.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gb;
  ctx.fillRect(0, h - edge, w, edge);
  // Left
  const gl = ctx.createLinearGradient(0, 0, edge, 0);
  gl.addColorStop(0, 'rgba(255,255,255,0)');
  gl.addColorStop(1, 'rgba(255,255,255,1)');
  ctx.fillStyle = gl;
  ctx.fillRect(0, 0, edge, h);
  // Right
  const gr = ctx.createLinearGradient(w - edge, 0, w, 0);
  gr.addColorStop(0, 'rgba(255,255,255,1)');
  gr.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gr;
  ctx.fillRect(w - edge, 0, edge, h);
  scene.textures.addCanvas(key, canvas);
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
  return key;
}

/** Ensure a rectangular ring (stroke-only) texture. */
function ensureRectEdgeTexture(
  scene: Phaser.Scene,
  w: number,
  h: number,
  lineWidth: number,
): string {
  const key = `_zone-rect-edge-${w}x${h}-w${lineWidth}`;
  if (scene.textures.exists(key)) return key;
  const pad = lineWidth;
  const cw = w + pad * 2;
  const ch = h + pad * 2;
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d')!;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = lineWidth;
  ctx.strokeRect(pad, pad, w, h);
  scene.textures.addCanvas(key, canvas);
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
  return key;
}

export class ZoneOverlay extends VfxPrimitive<ZoneOverlayConfig> {
  private fillKey!: string;
  private edgeKey!: string;
  private fillImg!: Phaser.GameObjects.Image;
  private edgeImg!: Phaser.GameObjects.Image;
  private pulseTween: Phaser.Tweens.Tween | null = null;
  private playing = false;
  private inWarning = false;

  protected allocatePool(): void {
    const { config } = this;
    const shape = config.shape;

    if (shape === 'circle' || shape === 'ring') {
      const r = (config.radius ?? config.textureSize) * CRISP_SCALE;
      this.fillKey = ensureSoftCircleTexture(this.scene, r);
      const edgeLw = config.edgeLineWidth * CRISP_SCALE;
      this.edgeKey = ensureRingTexture(this.scene, r, edgeLw);
    } else {
      // rectangle
      const w = (config.width ?? config.textureSize * 2) * CRISP_SCALE;
      const h = (config.height ?? config.textureSize) * CRISP_SCALE;
      this.fillKey = ensureRectFillTexture(this.scene, w, h);
      const edgeLw = config.edgeLineWidth * CRISP_SCALE;
      this.edgeKey = ensureRectEdgeTexture(this.scene, w, h, edgeLw);
    }

    this.fillImg = this.createPoolImage(this.fillKey);
    this.fillImg.setTint(config.fillTint);

    this.edgeImg = this.createPoolImage(this.edgeKey);
    this.edgeImg.setTint(config.edgeTint);
  }

  /** Show zone in warning state (fast pulse, high alpha). */
  playWarning(x: number, y: number, depth: number): void {
    this.playing = true;
    this.inWarning = true;

    const d = depth + (this.config.depthOffset ?? 0);
    this.activate(this.fillImg, x, y, d);
    this.activate(this.edgeImg, x, y, d + 0.05);

    const warnAlpha = this.config.warningAlpha ?? this.config.baseAlpha * 1.6;
    this.fillImg.setAlpha(warnAlpha);
    this.edgeImg.setAlpha(this.config.edgeLineWidth > 0 ? 0.8 : 0);

    this.startPulse(
      this.config.warningPulsePeriodMs ?? this.config.pulsePeriodMs / 2,
      warnAlpha,
    );
  }

  play(x: number, y: number, depth: number): void {
    const wasWarning = this.inWarning;
    this.playing = true;
    this.inWarning = false;

    const d = depth + (this.config.depthOffset ?? 0);

    if (!wasWarning) {
      this.activate(this.fillImg, x, y, d);
      this.activate(this.edgeImg, x, y, d + 0.05);
    } else {
      // Already visible from warning — just reposition
      this.fillImg.setPosition(x, y).setDepth(d);
      this.edgeImg.setPosition(x, y).setDepth(d + 0.05);
    }

    this.fillImg.setAlpha(this.config.baseAlpha);
    this.edgeImg.setAlpha(this.config.edgeLineWidth > 0 ? 0.6 : 0);

    this.startPulse(this.config.pulsePeriodMs, this.config.baseAlpha);
  }

  stop(): void {
    this.playing = false;
    this.inWarning = false;
    this.killPulseTween();

    const targets = [this.fillImg, this.edgeImg].filter(
      (img) => img.active,
    );
    if (targets.length === 0) return;

    this.scene.tweens.add({
      targets,
      alpha: 0,
      duration: this.config.fadeDurationMs,
      ease: 'Sine.easeOut',
      onComplete: () => {
        for (const img of targets) this.deactivate(img);
      },
    });
  }

  updatePosition(x: number, y: number, depth: number, _delta: number): void {
    if (!this.playing) return;
    const d = depth + (this.config.depthOffset ?? 0);
    this.fillImg.setPosition(x, y).setDepth(d);
    this.edgeImg.setPosition(x, y).setDepth(d + 0.05);
  }

  private startPulse(periodMs: number, centerAlpha: number): void {
    this.killPulseTween();
    const range = this.config.pulseAlphaRange;

    this.pulseTween = this.scene.tweens.add({
      targets: this.fillImg,
      alpha: { from: centerAlpha - range, to: centerAlpha + range },
      duration: periodMs / 2,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private killPulseTween(): void {
    if (this.pulseTween) {
      this.pulseTween.destroy();
      this.pulseTween = null;
    }
  }
}
